import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { APP_PATHS, PAGINATION_DEFAULT } from '@core/constants';
import { explainedInPlace, toRefusal } from '@core/helpers/installation-refusal.helper';
import { locationUpdateRefusalMessage } from '@core/helpers/location-update-refusal.helper';
import {
  DeviceResponseModel,
  LocationCreation,
  LocationFilterModel,
  LocationPayloadModel,
  LocationResponseModel,
  LocationModel,
  LocationUploadResponseModel,
  LocationUpdateModel,
} from '@core/models';
import { DeviceService, LocationService } from '@core/services';
import { TranslateService } from '@ngx-translate/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { isNumber, isString, pickBy } from 'lodash';
import { pipe, switchMap, tap } from 'rxjs';
import { linkRefusal } from '../pages/installations/link-refusal';
import { linkResult, linkResultMessage } from '../pages/installations/link-result';

type LocationState = {
  isLoading: boolean;
  isLoadingLocationDevices: boolean;
  locationData: LocationResponseModel;
  locationDetail: LocationModel;
  filter: LocationFilterModel;
  devicesByLocationId: DeviceResponseModel;
  locationsUploadSuccess: LocationUploadResponseModel[];
  // Counts the links that may have changed the installations at the location, so the card that
  // lists them reads them again.
  installationsRevision: number;
};

const initialState: LocationState = {
  isLoading: false,
  isLoadingLocationDevices: false,
  locationData: {
    locations: [],
    numberOfItems: 0,
  },
  locationDetail: {
    locationId: 0,
    locationType: '',
    locationStreetNumber: 0,
    locationStreet: '',
    locationStreetNumberAddition: '',
    locationPostalCode: '',
    locationCity: '',
    locationLatitude: '',
    locationLongitude: '',
    customers: [],
  },
  filter: {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
  },
  devicesByLocationId: {
    devices: [],
    numberOfItems: 0,
  },
  locationsUploadSuccess: [],
  installationsRevision: 0,
};

export const LocationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      locationService = inject(LocationService),
      deviceService = inject(DeviceService),
      snackBar = inject(MatSnackBar),
      translate = inject(TranslateService),
      router = inject(Router),
    ) => ({
      refreshLocationsMethod: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() => {
            return locationService.getListLocationsFilter(store.filter()).pipe(
              tapResponse({
                next: (data: LocationResponseModel) => {
                  patchState(store, {
                    locationData: data,
                    isLoading: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      getLocationsMethod: rxMethod<number>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap((pageIndex: number) => {
            const filter: LocationFilterModel = {
              ...store.filter(),
              offset: pageIndex,
            };

            patchState(store, { filter });
            return locationService.getListLocationsFilter(filter).pipe(
              tapResponse({
                next: (data: LocationResponseModel) => {
                  patchState(store, {
                    locationData: data,
                    filter,
                    isLoading: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      getLocationsFilterMethod: rxMethod<LocationPayloadModel>(
        pipe(
          tap(() =>
            patchState(store, { isLoading: true, locationData: initialState.locationData }),
          ),
          switchMap(payload => {
            let filter = {
              ...store.filter(),
              ...payload,
              offset: initialState.filter.offset,
            };
            filter = pickBy(
              filter,
              (value: any) =>
                (isNumber(value) && value !== undefined) || (isString(value) && value !== ''),
            ) as any;
            patchState(store, { filter });
            return locationService.getListLocationsFilter(filter).pipe(
              tapResponse({
                next: (data: any) => {
                  patchState(store, {
                    locationData: data,
                    filter,
                    isLoading: false,
                  });
                },
                error: () => {},
              }),
            );
          }),
        ),
      ),

      getLocationDetailMethod: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(locationId => {
            return locationService.getLocationDetail(locationId).pipe(
              tapResponse({
                next: (data: any) => {
                  patchState(store, { locationDetail: data, isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      createLocationMethod: rxMethod<{
        location: LocationCreation;
        partnerOid: string;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload => {
            return locationService.createLocation(payload.location, payload.partnerOid).pipe(
              tapResponse({
                next: () => {
                  const locationsUploadSuccess: LocationUploadResponseModel[] = [
                    {
                      ...payload.location,
                      validation_status: 'Ok',
                    },
                  ];
                  patchState(store, {
                    isLoading: false,
                    locationsUploadSuccess,
                  });
                },
                error: () => {
                  const locationsUploadSuccess: LocationUploadResponseModel[] = [
                    {
                      ...payload.location,
                      validation_status: 'Fail',
                    },
                  ];
                  patchState(store, {
                    isLoading: false,
                    locationsUploadSuccess,
                  });
                },
              }),
            );
          }),
        ),
      ),

      updateLocationMethod: rxMethod<{
        location: LocationUpdateModel;
        locationId: number;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload => {
            const locationUpdated: LocationModel = {
              ...store.locationDetail(),
              locationStreet: payload.location.street,
              locationStreetNumber: payload.location.street_number,
              locationStreetNumberAddition: payload.location.street_number_addition,
              locationPostalCode: payload.location.postalcode,
              locationCity: payload.location.city,
            };

            return locationService.updateLocation(payload.location, payload.locationId).pipe(
              tapResponse({
                next: () => {
                  patchState(store, {
                    locationDetail: locationUpdated,
                    isLoading: false,
                  });
                },
                error: error => {
                  patchState(store, {
                    isLoading: false,
                  });
                  const message = locationUpdateRefusalMessage(toRefusal(error));
                  if (message) {
                    snackBar.open(translate.instant(message.key, message.params), '', {
                      horizontalPosition: 'right',
                      verticalPosition: 'top',
                      duration: 8000,
                    });
                  }
                },
              }),
            );
          }),
        ),
      ),

      createLocationMultipleMethod: rxMethod<{
        locations: LocationCreation[];
        partnerOid: string;
      }>(
        pipe(
          tap(payload => {
            const locationCreation = payload.locations.map(
              location =>
                ({
                  ...location,
                  validation_status: 'Fail',
                }) as LocationCreation,
            );
            patchState(store, { locationsUploadSuccess: locationCreation });
          }),
          switchMap(payload => {
            return payload.locations.map((location, index) => {
              return locationService
                .createLocation(location, payload.partnerOid)
                .pipe(
                  tapResponse({
                    next: () => {
                      const locationsUploadSuccess = store.locationsUploadSuccess().map((l, i) => ({
                        ...l,
                        validation_status: index === i ? 'Ok' : l.validation_status,
                      }));
                      patchState(store, {
                        isLoading: false,
                        locationsUploadSuccess,
                      });
                    },
                    error: () => {
                      const locationsUploadSuccess = store.locationsUploadSuccess().map((l, i) => ({
                        ...l,
                        validation_status: index === i ? 'Fail' : l.validation_status,
                      }));
                      patchState(store, {
                        isLoading: false,
                        locationsUploadSuccess,
                      });
                    },
                  }),
                )
                .subscribe();
            });
          }),
        ),
      ),

      createLocationBulkMethod: rxMethod<{ files: File[]; partnerOid: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(payload => {
            let count = 0;
            return payload.files.map(file => {
              return locationService
                .createLocationBulk(file, payload.partnerOid)
                .pipe(
                  tapResponse({
                    next: (data: LocationUploadResponseModel[]) => {
                      count++;
                      patchState(store, {
                        locationsUploadSuccess: store.locationsUploadSuccess().concat(data),
                      });

                      if (count === payload.files.length) {
                        patchState(store, {
                          isLoading: false,
                        });
                      }
                    },
                    error: () => {
                      patchState(store, { isLoading: false });
                    },
                  }),
                )
                .subscribe();
            });
          }),
        ),
      ),

      getDevicesByLocationIdMethod: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, { isLoadingLocationDevices: true, devicesByLocationId: undefined }),
          ),
          switchMap(locationId => {
            return deviceService.getDevicesByLocationId(locationId).pipe(
              tapResponse({
                next: (data: DeviceResponseModel) => {
                  patchState(store, {
                    devicesByLocationId: data,
                    isLoadingLocationDevices: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoadingLocationDevices: false });
                },
              }),
            );
          }),
        ),
      ),

      /**
       * Links an existing device to the location, then refreshes the location's device list. With
       * `explainResult` the user chose where the device goes, so the answer says what became of
       * the installation, and a refusal is explained rather than shown as the API's bare reason.
       */
      provisionDeviceMethod: rxMethod<{
        deviceId: string;
        locationId: string | number;
        installerOid: string;
        installationOid?: string;
        explainResult?: boolean;
      }>(
        pipe(
          tap(() => patchState(store, { isLoadingLocationDevices: true })),
          switchMap(({ deviceId, locationId, installerOid, installationOid, explainResult }) =>
            deviceService
              .provisionDevice(
                deviceId,
                locationId,
                installerOid,
                undefined,
                installationOid,
                explainResult ? explainedInPlace() : undefined,
              )
              .pipe(
                tap({
                  next: response => {
                    if (!explainResult) return;
                    patchState(store, { installationsRevision: store.installationsRevision() + 1 });
                    const result = linkResult(response, deviceId);
                    if (!result) return;
                    const { key, params } = linkResultMessage(result);
                    const toChecklist = result.boundCount === 0;
                    const ref = snackBar.open(
                      translate.instant(key, params),
                      toChecklist ? translate.instant('installations.link.chooseSources') : '',
                      { horizontalPosition: 'right', verticalPosition: 'top', duration: 10000 },
                    );
                    if (toChecklist) {
                      ref.onAction().subscribe(() => {
                        void router.navigate([
                          '/',
                          APP_PATHS.Installations,
                          result.installationOid,
                          'data-sources',
                        ]);
                      });
                    }
                  },
                  error: error => {
                    if (!explainResult) return;
                    snackBar.open(translate.instant(linkRefusal(toRefusal(error)).messageKey), '', {
                      horizontalPosition: 'right',
                      verticalPosition: 'top',
                      duration: 8000,
                    });
                  },
                }),
                switchMap(() => deviceService.getDevicesByLocationId(String(locationId))),
                tapResponse({
                  next: (data: DeviceResponseModel) => {
                    patchState(store, {
                      devicesByLocationId: data,
                      isLoadingLocationDevices: false,
                    });
                  },
                  error: () => {
                    patchState(store, { isLoadingLocationDevices: false });
                  },
                }),
              ),
          ),
        ),
      ),
    }),
  ),
);
